import { MngActionDef } from '@mng/mng-inputs';
import { getKundeInfoAsString, IDienstleistung, IGesundheitsProgrammInfo } from '@mng/mgs-lib';
import {
  createActionItemGroups,
  createActionItems,
  dienstleistungItem,
  IActionContext,
  IActionItem
} from './home/action-items';
import { SetSidenavActionDefs } from '@mng/mng-frame';
import { Store } from '@ngxs/store';
import { computed, effect, inject, Injector, Service, signal, untracked } from '@angular/core';
import { MngConfigService } from '@mng/mng-config';
import { getDistinctObjects } from '@mng/mng-ts-helpers';
import { ProgrammService } from './programm/programm.service';
import { MgsEnvironmentInformationService } from './mgs-environment-information.service';
import { DienstleistungDemoService } from './dienstleistung/dienstleistung-demo/dienstleistung-demo.service';
import { AuthStateFacade } from './auth/auth-state.facade';

/**
 * Dient für den aktuellen State abhängig UI Daten bereitzustellen.
 *
 * z.B. Aufbau Sidenav
 */

export interface IMgsActiveState {
  actionDefs: MngActionDef[];
  dienstleistung?: IDienstleistung;
}

@Service()
export class MgsActiveState {
  readonly authState = inject(AuthStateFacade);
  readonly store = inject(Store);

  readonly injector = inject(Injector);

  readonly envInfoService = inject(MgsEnvironmentInformationService);

  readonly version = computed(() => this.envInfoService.environmentInformation()?.service.version ?? '-');

  readonly dienstleistungDemoService = inject(DienstleistungDemoService);

  // MngConfigService muss "injected" werden damit auch der entsprechende "store" befüllt wird (in MngConfigService)
  readonly configService = inject(MngConfigService);

  readonly programmService = inject(ProgrammService);

  readonly activeState = signal<IMgsActiveState>({ actionDefs: [] });

  readonly partnerDienstleistungsartIds = computed(() =>
    getDistinctObjects(
      this.programmService.partnerProgramme(),
      (programm: IGesundheitsProgrammInfo) => programm.dienstleistungsArtId
    ).map((programm) => programm.dienstleistungsArtId)
  );

  readonly actionContext = computed<IActionContext>(() => ({
    user: this.authState.user(),
    userRoles: this.authState.userRoles(),
    programme: this.programmService.partnerProgramme(),
    demoProgramme: this.dienstleistungDemoService.aktiveDemoProgramme()
  }));

  readonly actionItems = computed<IActionItem[]>(() => createActionItems(this.actionContext()));
  readonly actionItemGroups = computed(() => createActionItemGroups(this.actionItems()));

  readonly actionDefs = computed<MngActionDef[]>(() => {
    let actionDefs: MngActionDef[] = [];
    if (this.authState.authenticated()) {
      if (!this.authState.isKidsTerminUser()) {
        actionDefs.push(new MngActionDef().withTitle('Startseite').withTarget('/').withIconName('home-outline'));
      }

      const userActionDefs = this.actionItems().map((item) => this.createActionDef(item));
      actionDefs = [...actionDefs, ...userActionDefs];
    }

    return actionDefs;
  });

  constructor() {
    effect(() => {
      const actionDefs = this.actionDefs();
      untracked(() => {
        this.store.dispatch(new SetSidenavActionDefs(actionDefs));
      });
    });

    effect(() => {
      const user = this.envInfoService.environmentInformation()?.clientProps.user;
      untracked(() => {
        this.authState.updateUser(user);
      });
    });
  }

  async initialize() {
    await this.programmService.initialize();
  }

  updateDienstleistung(dienstleistung: IDienstleistung) {
    this.activeState.update((state) => ({ ...state, dienstleistung }));
  }

  createDienstleistungActionDef(item: IActionItem): MngActionDef {
    if (this.activeState().dienstleistung) {
      const dienstleistung = this.activeState().dienstleistung;

      const title = `${dienstleistung.code} (${getKundeInfoAsString(dienstleistung.kunde)})`;

      const target = `${item.route}/${this.activeState().dienstleistung.code}`;

      return new MngActionDef().withTitle(title).withTarget(target).withIconName(item.svgIcon);
    } else {
      return new MngActionDef().withTitle(item.label).withTarget(item.route).withIconName(item.svgIcon);
    }
  }

  createActionDef(item: IActionItem): MngActionDef {
    if (dienstleistungItem.label === item.label) {
      // Spezialbehandlung für Dienstleistung
      return this.createDienstleistungActionDef(item);
    } else {
      const actionDef = new MngActionDef().withTitle(item.label).withIconName(item.svgIcon).withGroup(item.group);
      if (item.route) {
        actionDef.withTarget(item.route);
      }
      if (item.action) {
        const actionFunc = () => item.action(this.injector);

        actionDef.withAction(actionFunc);
      }
      return actionDef;
    }
  }
}
